import { useEffect, useState, useCallback, FormEvent } from 'react';

import { Banner, Text, Link, Button, VSpacing, useBreakpoint } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import defaultRequestErrorHandler from 'src/api/notifications/defaultRequestErrorHandler';
import Form from 'src/components/Form';
import { useNotification } from 'src/components/Notifications/Provider';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';

import createSearchSubscription from 'src/components/Search/Resume/Banners/SavedSearchSubscription/api';

enum STATE {
    SAVING = 'saving',
    SAVED = 'saved',
}

const TrlKeys = {
    teaser1: 'serp.savedSearch.teaser.1',
    teaser2: 'serp.savedSearch.teaser.2',
    subscribe: 'search.vacancy.savedSearch.subscribe',
    saved: 'vacancySearchResults.saveSearchPreference.searchSavedIn',
    autosearch: 'vacancySearchResults.saveSearchPreference.autosearch',
};

const SavedSearchSubscription: TranslatedComponent = ({ trls }) => {
    const query = useSelector((state) => state.resumeSearchResult.queries?.autosearch || '');
    const saveSearch = useSelector((state) => state.resumeSearchResult.saveSearch);

    const [state, setState] = useState<STATE | null>(null);
    const [disabled, setDisabled] = useState(true);
    const location = useSelector((state) => state.router.location);
    const { addNotification } = useNotification();

    const { isMobile } = useBreakpoint();

    useEffect(() => {
        setDisabled(false);
    }, [setDisabled]);

    useEffect(() => {
        setState(null);
    }, [location, setState]);

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>): void => {
            event.preventDefault();

            setState(STATE.SAVING);
            createSearchSubscription(query)
                .catch((error) => {
                    defaultRequestErrorHandler(error, addNotification);
                    setState(null);
                })
                .finally(() => {
                    setState(STATE.SAVED);
                });
        },
        [setState, query, addNotification]
    );

    if (!saveSearch) {
        return null;
    }

    const isSaved = state === STATE.SAVED;

    return (
        <>
            <Form onSubmit={handleSubmit} data-qa="autosearch-subscribe__form">
                <Banner
                    stretched
                    visible
                    showBorder
                    showClose={false}
                    layout="horizontal"
                    buttonBase={
                        !isSaved ? (
                            <Button
                                size="small"
                                mode="primary"
                                style="accent"
                                stretched={isMobile}
                                disabled={disabled}
                                loading={state === STATE.SAVING}
                                data-qa="autosearch-subscribe__submit"
                                type="submit"
                            >
                                {trls[TrlKeys.subscribe]}
                            </Button>
                        ) : undefined
                    }
                    title={!isSaved ? <Text typography="title-5-semibold">{trls[TrlKeys.teaser1]}</Text> : undefined}
                    content={
                        <Text typography="paragraph-2-regular">
                            {trls[TrlKeys[isSaved ? 'saved' : 'teaser2']]}
                            {isSaved && (
                                <Link
                                    style="accent"
                                    typography="label-2-regular"
                                    Element={SPALink}
                                    to="/resumesavedsearch"
                                    enableVisited={false}
                                    inline
                                >
                                    {trls[TrlKeys.autosearch]}
                                </Link>
                            )}
                        </Text>
                    }
                />
            </Form>
            <VSpacing default={16} />
        </>
    );
};

export default translation(SavedSearchSubscription);
